import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LanguageSelector } from '@/components/LanguageSelector';
import { buildPathForLocale } from '@/lib/intl-seo';

const SellerRegistrationPendingPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { email, sellerType, category } = location.state || {};
  const lc = (i18n.language || 'en').substring(0, 2);
  const homePath = buildPathForLocale(lc, []);
  const loginPath = buildPathForLocale(lc, ['login']);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-background to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8 flex flex-col items-center gap-4">
          <Link to={homePath} className="inline-flex items-center gap-2">
            <img src="/logo.png" alt="Turkish E-Market" className="h-10 w-auto" />
            <span className="text-2xl font-serif font-bold text-primary">Turkish E-Market</span>
          </Link>
          <LanguageSelector variant="compact" />
        </div>

        <Card className="border-amber-200">
          <CardContent className="p-8 text-center">
            {/* Icon */}
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold mb-2 text-amber-800">
              {t('seller.pending.title')}
            </h1>
            
            <p className="text-muted-foreground mb-6">
              {t('seller.pending.subtitle')}
            </p>

            {/* Steps */}
            <div className="bg-amber-50 rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold mb-4 text-amber-800">{t('seller.pending.nextSteps')}</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">{t('seller.pending.step1')}</p>
                    <p className="text-sm text-muted-foreground">{t('seller.pending.step1Desc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">{t('seller.pending.step2')}</p>
                    <p className="text-sm text-muted-foreground">{t('seller.pending.step2Desc')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">{t('seller.pending.step3')}</p>
                    <p className="text-sm text-muted-foreground">{t('seller.pending.step3Desc')}</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Email info */}
            {email && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-muted-foreground mb-1">{t('seller.pending.registeredEmail')}</p>
                <p className="font-medium">{email}</p>
              </div>
            )}

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>{t('seller.pending.note')}:</strong> {t('seller.pending.noteDesc')}
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <Button asChild className="w-full rounded-full">
                <Link to={homePath}>
                  {t('common.backToHome')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full rounded-full">
                <Link to={loginPath}>
                  {t('auth.signIn')}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('seller.pending.questions')}{' '}
          <a href="mailto:bilgi@turkishemarket.com" className="text-primary hover:underline">
            bilgi@turkishemarket.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default SellerRegistrationPendingPage;
